import type { EventName } from './events'
import {
  ENTITY_STEMS,
  ENUM_VALUES,
  ENUMS,
  FLAGS,
  FRACTIONAL,
  ISO_KEYS,
  MEASUREMENTS,
  STANDALONE,
  STEMS,
} from './vocabulary'

/**
 * Analytics, with the privacy boundary enforced rather than reviewed.
 *
 * CLAUDE.md: "Analytics carries counts, durations, enums and booleans only.
 * Never amounts, merchant or category names, account masks, or user-typed text.
 * `query_length`, not the query."
 *
 * **The point of this module is the guard, not the transport.** Any library can
 * send an event; the risk is sending a merchant name or a balance to a third
 * party, and that has to be impossible by construction. A review catches it the
 * first four times.
 *
 * **The key check is an ALLOW-list, and that is load-bearing.** Two earlier
 * versions were deny-lists and web-tester defeated both: the first leaked 41 of
 * 44 sensitive payloads, the second — already corrected once — still leaked 7,
 * including `card`, `ssn`, `threshold: '118.4'` and a nested object carrying an
 * email and a merchant name together. Neither round failed for want of a word.
 * A finite list of forbidden names cannot cover an unbounded space of names
 * somebody might choose, so a deny-list is review performed in advance by
 * someone who had to guess — which is exactly what the plan says this must not
 * be. See `vocabulary.ts` for the vocabulary and the argument.
 *
 * Two independent gates, and both must pass:
 *
 *   1. the KEY is registered in `keys.ts` — a stem plus a measurement or flag
 *      suffix, or a standalone enum name
 *   2. the VALUE is a primitive of an admissible shape — a whole number unless
 *      the key says it is a rate, or a short lower-case identifier that is not
 *      a number in disguise
 *
 * So a mistake in the key vocabulary is not on its own a leak: `query_bucket` is
 * a registered key, and `query_bucket: 'coffee shops'` is still rejected, on the
 * space in the value.
 */

type Primitive = string | number | boolean | null | undefined
export type Props = Record<string, Primitive>

/**
 * What a safe string value looks like: an identifier, an enum member, a bucket
 * name. Lower case, no spaces.
 *
 * An allow-list here too. The 40-character ceiling this replaced was
 * structurally incapable of catching short PII — "Royal Bank", "rent cheque"
 * are well under it — and capitals and spaces are what separate those from
 * `date_desc` and `p90`.
 */
const IDENTIFIER = /^[a-z0-9][a-z0-9_.-]*$/

/**
 * ISO-style codes, the one place capitals are admissible.
 *
 * `CAD`, `USD`, `en-CA`, `GB`. Excluded by `IDENTIFIER` and they are the most
 * standard enum values that exist, so refusing them does not make anything
 * safer — it makes a developer rename the key to get the value through, which
 * is the failure mode a guard can least afford. Bounded to letters only and at
 * most eight characters, so it admits no prose.
 */
const ISO_CODE = /^[A-Za-z]{2,3}(-[A-Za-z]{2,4})?$/

/**
 * A ceiling as well, for a long lower-case run the pattern would accept.
 *
 * 32 rather than 40 because web-tester's fuzz put a 40-character run under a
 * registered key and sat exactly on the old boundary. Any such number is
 * arbitrary; it is the secondary gate, and the key registry is the primary one.
 */
const MAX_VALUE_LENGTH = 32

/** Underscore segments, with camelCase normalised first. Lower-casing before
 *  the split erases the boundaries the split depends on — which is how a first
 *  version let `merchantName` through while passing every underscore case. */
function segmentsOf(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[_\-.]/)
    .filter(Boolean)
}

/** The last segment, which is what decides whether a key is a measurement. */
function finalSegment(key: string): string | undefined {
  return segmentsOf(key).at(-1)
}

function assertKeyRegistered(key: string): void {
  const segments = segmentsOf(key)
  const normalised = segments.join('_')
  if (STANDALONE.has(normalised)) return

  const last = segments.at(-1)
  const stem = segments.slice(0, -1).join('_')
  // An entity stem takes a measurement or a flag, never an enum and never a
  // bucket: `merchant_count` is a number, while `merchant_type` and
  // `merchant_bucket` are both slots shaped like a merchant name. `_bucket` is
  // the one measurement exempt from the numeric rule — a bucket really is a
  // label — which is exactly why it may not sit on an entity, where the label
  // IS the datum. `render_bucket` and `duration_bucket` are unaffected (#113).
  const entity = ENTITY_STEMS.has(stem)
  const measurementAllowed =
    last !== undefined && MEASUREMENTS.has(last) && !(entity && last === 'bucket')
  const enumAllowed = last !== undefined && ENUMS.has(last) && !entity
  const suffixed =
    last !== undefined && (measurementAllowed || FLAGS.has(last) || enumAllowed)
  if (suffixed && STEMS.has(stem)) return

  throw new Error(
    `analytics: property "${key}" is not allowed — it is not registered in ` +
      `platform/analytics/vocabulary.ts. Register a stem plus a measurement suffix ` +
      `(merchant_count), or a standalone enum name — never the thing itself.`,
  )
}

function assertValueSafe(key: string, value: Primitive): void {
  const reject = (why: string): never => {
    throw new Error(`analytics: property "${key}" ${why}`)
  }

  // Absent is absent. A count that was not measured is not a string in a
  // number's slot, so this precedes the typing rules below rather than sitting
  // after them — where it rejected `total_count: null` and `gate_shown: null`,
  // two entries on web-tester's list of calls that must keep working.
  if (value === null || value === undefined) return

  // A key whose name says `count`, `length` or `ms` has to carry a number, and a
  // key whose name says `active` has to carry a boolean. Otherwise the slot most
  // obviously reserved for a number is the easiest place to hide a value —
  // `query_length: 'coffee'` and `merchant_count: 'tim-hortons'` both passed.
  // Costs nothing legitimate; `_bucket` is exempt because a bucket really is a
  // label. web-architect's follow-up B, and the cheapest thing in the review.
  const last = finalSegment(key)
  if (last !== undefined && MEASUREMENTS.has(last) && last !== 'bucket') {
    if (typeof value !== 'number') reject(`must be a number, because "${last}" is a measurement`)
  }
  if (last !== undefined && FLAGS.has(last) && typeof value !== 'boolean') {
    reject(`must be a boolean, because "${last}" is a flag`)
  }

  if (typeof value === 'boolean') return

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) reject('is not a finite number')
    // Money is integer cents (CLAUDE.md), so a DECIMAL is money unless the key
    // says it is a rate. An integer under a registered key is a count by
    // construction, which is what the key registry buys.
    if (!Number.isInteger(value)) {
      if (last === undefined || !FRACTIONAL.has(last)) {
        reject('is fractional, which is money unless the key says it is a rate')
      }
    }
    return
  }

  if (typeof value !== 'string') {
    // An object or an array is never checked by the two branches above, so it
    // used to pass untouched — `{ user: { email, merchant_name } }` sent both.
    // `Props` blocks it at compile time, which is no protection at all through a
    // cast, an `any`, or a props object assembled from component state.
    reject(`is a ${Array.isArray(value) ? 'array' : typeof value}, not a primitive`)
  }

  // A key with a closed value set takes exactly that set, however identifier-shaped the value is.
  const closed: Readonly<Record<string, readonly string[]>> = ENUM_VALUES
  const allowed = closed[segmentsOf(key).join('_')]
  if (allowed !== undefined && !allowed.includes(value)) {
    reject('is not one of the values this key allows')
  }

  const isoAllowed = ISO_KEYS.has(segmentsOf(key).join('_')) && ISO_CODE.test(value)
  if (!IDENTIFIER.test(value) && !isoAllowed) {
    reject('is not an identifier — capitals or spaces mean prose, and prose means user data')
  }
  if (value.length > MAX_VALUE_LENGTH) {
    reject(`is ${value.length} characters, past the ${MAX_VALUE_LENGTH} an enum member needs`)
  }
  // A number sent as a string dodges the numeric arm above: '1204', '118.4' and
  // '118.444' are all money that the shape checks cannot see.
  if (!Number.isNaN(Number(value))) {
    reject('is a number written as a string — send it as a number so it is range-checked')
  }
}

type Sink = (name: string, props: Props) => void

/**
 * A no-op until `initAnalytics` installs a real one (#112).
 *
 * Not a throw: an analytics call that crashes a page is a worse outcome than one
 * that is lost, and this is the state every test that does not install a double
 * runs in.
 */
const NO_OP: Sink = () => {}
let sink: Sink = NO_OP

/** Test seam. Production code calls `initAnalytics`. */
export function __setSink(fn: Sink): void {
  sink = fn
}

/** Restore the no-op. Tests that install a sink must call this in `afterEach`,
 *  or a later file inherits a sink pushing into an array it cannot see. */
export function __resetSink(): void {
  sink = NO_OP
}

/**
 * Send an event.
 *
 * Every property is checked BEFORE the sink is called — a guard that threw after
 * sending would be reporting the leak it had just caused — and every property is
 * checked, not just the first.
 *
 * **In development a violation throws; in production the event is dropped and
 * logged.** The privacy property is identical either way, because in both the
 * property never reaches the sink. What differs is the blast radius: the
 * `NO_OP` sink above exists because a lost event beats a crashed page, and a
 * guard that crashes the route it protects contradicts it.
 */
export function emit(name: EventName, props: Props = {}): void {
  // Built as we validate, and it is this COPY that is sent.
  //
  // Forwarding the caller's object made validation cover a moment rather than
  // the payload: mutating `props` after the call changed what an asynchronous
  // sink serialised, and a getter could return one value to the validator and
  // another to Firebase. Reading each property once and sending that read makes
  // the two agree by construction. It also drops symbol-keyed and
  // non-enumerable properties, which `Object.entries` never validated.
  const checked: Props = {}
  try {
    for (const [key, value] of Object.entries(props)) {
      assertKeyRegistered(key)
      assertValueSafe(key, value)
      checked[key] = value
    }
  } catch (error) {
    // `?.` because this is the guard's own failure branch and it must not be
    // the thing that crashes. `import.meta.env` is undefined in any runtime
    // Vite has not compiled — a node script, a worker, SSR — and the unguarded
    // read threw a TypeError that REPLACED the real message: every rejection
    // reported "Cannot read properties of undefined (reading 'DEV')" instead of
    // which property was refused. Found by web-architect probing outside vitest.
    if (import.meta.env?.DEV) throw error
    console.error(error)
    return
  }
  sink(name, checked)
}

/**
 * SHA-256 of the **uppercased** id, hex, unsalted, untruncated.
 *
 * Master PRD D19. iOS's `AnalyticsService.swift:70` hashes what
 * `AuthStateManager.swift:204` gives it, which is `user.id.uuidString` — and
 * Swift's `UUID.uuidString` is uppercase. FastAPI returns the same UUID
 * lowercase.
 *
 * This is the D9 casing hazard applied to analytics rather than billing, and it
 * fails far more quietly: there is no double charge to expose it. Hash the
 * lowercase id and every web user is a different person from their iOS self in
 * every dashboard, permanently and undetectably. The plan's implementation omits
 * the uppercasing and so fails its own pinned digest.
 *
 * No caller yet — `setAnalyticsUser` lands with the auth boundary in #112.
 */
export async function hashUserId(id: string): Promise<string> {
  const bytes = new TextEncoder().encode(id.toUpperCase())
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * The last id handed to the sink. Test seam — `analytics.test.ts` asserts the
 * raw UUID never appears, and a private variable cannot be checked.
 */
let analyticsUser: string | null = null

/**
 * Which `setAnalyticsUser` call is still the current one.
 *
 * Hashing is async and clearing is not, so completion order and call order are
 * not the same thing: `setAnalyticsUser(id)` followed by `setAnalyticsUser(null)`
 * left the NULL in place first and then the hash landed on top of it, restoring
 * the identity of a user who had just signed out. web-tester found it on the
 * primitive. The provider calls this fire-and-forget on both edges, so nothing
 * upstream is ordering it either.
 */
let identitySequence = 0

export function __lastAnalyticsUser(): string | null {
  return analyticsUser
}

/**
 * Identify the user to analytics by the HASH of their id, never the id.
 *
 * `hashUserId` uppercases first — D19, and the same casing hazard as the
 * RevenueCat app-user-id. Getting it wrong here fails silently: there is no
 * double charge to expose it, just two populations that never join.
 *
 * Called on `signedIn` and cleared on sign-out.
 */
export async function setAnalyticsUser(id: string | null): Promise<void> {
  const ticket = (identitySequence += 1)
  const next = id === null ? null : await hashUserId(id)
  // Last CALLER wins, not last to finish. Without this the clear-then-hash
  // interleaving above silently re-identifies a signed-out user, which is the
  // one thing this function must not do.
  if (identitySequence !== ticket) return
  analyticsUser = next
  // After the stale-ticket check, so a superseded call never reaches a vendor.
  notifyIdentity(next)
}

type IdentitySink = (hashedId: string | null) => void

/** One per transport (Firebase, Sentry); each gets the hash, never the raw id. */
const identitySinks: IdentitySink[] = []

/** Never throws: an identity call must not crash the sign-in that triggered it. */
function notify(sink: IdentitySink, id: string | null): void {
  try {
    sink(id)
  } catch (error) {
    console.error(error)
  }
}

function notifyIdentity(id: string | null): void {
  for (const sink of identitySinks) notify(sink, id)
}

/**
 * Where the HASHED id goes (#112): Firebase `setUserId`, Sentry `setUser`. Additive,
 * because there are two transports and each installs independently.
 *
 * Replays the current identity to the new sink only, because the transports load
 * lazily and sign-in can win the race.
 */
export function addIdentitySink(fn: IdentitySink): void {
  identitySinks.push(fn)
  if (analyticsUser !== null) notify(fn, analyticsUser)
}

/** Remove every identity sink. Tests that add one call this in `afterEach`. */
export function __resetIdentitySinks(): void {
  identitySinks.length = 0
}
